/**
 * Diffs — §7.5, rendered from markdown and never from the block tree.
 *
 * ADR 0002 makes the JSON canonical and the markdown derived, and §5.1 puts the
 * diff on the derived side on purpose: a JSON tree diff reports that an
 * attribute moved between two objects, which is true and unreadable. The API
 * computes this at `GET /api/documents/:id/diff` and this file only paints it —
 * there is no diffing here, and there must never be, because a second
 * implementation would be a second answer to "what changed".
 *
 * Two states of {@link MarkdownDiff} are results rather than errors and are
 * rendered as such: `identical` (nothing changed, and saying so beats an empty
 * box) and `truncated` (the pair was too large to align, so the whole body is
 * reported as one replacement — "honest and useless, rather than a wrong
 * alignment, which would be useless and convincing").
 */

import type { DiffHunk, DiffLine, DiffResponse, MarkdownDiff } from '../../api/types.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { cn } from '../../components/ui/cn.ts';
import { Alert } from '../../components/ui/feedback.tsx';

const LINE_TONES: Record<DiffLine['type'], string> = {
  context: 'text-secondary',
  added: 'bg-success-primary text-success-primary',
  removed: 'bg-error-primary text-error-primary',
};

const MARKS: Record<DiffLine['type'], string> = { context: ' ', added: '+', removed: '-' };

function Line({ line }: { line: DiffLine }) {
  return (
    <tr className={LINE_TONES[line.type]}>
      <td className="w-12 border-r border-secondary px-2 text-right align-top text-xs text-quaternary select-none">
        {line.fromLine ?? ''}
      </td>
      <td className="w-12 border-r border-secondary px-2 text-right align-top text-xs text-quaternary select-none">
        {line.toLine ?? ''}
      </td>
      <td className="w-6 px-1 text-center align-top font-mono text-xs select-none">
        {MARKS[line.type]}
      </td>
      <td className="px-2 py-0.5 font-mono text-xs whitespace-pre-wrap">
        {/* An empty line still needs height, or a deletion of a blank line
            collapses and the two sides stop lining up. */}
        {line.text === '' ? ' ' : line.text}
      </td>
    </tr>
  );
}

function Hunk({ hunk }: { hunk: DiffHunk }) {
  return (
    <>
      <tr>
        <td
          colSpan={4}
          className="bg-secondary px-2 py-1 font-mono text-xs text-tertiary select-none"
        >
          @@ -{hunk.fromStart},{hunk.fromLines} +{hunk.toStart},{hunk.toLines} @@
        </td>
      </tr>
      {hunk.lines.map((line) => (
        // The pair is the identity. Either half can be null — an added line has
        // no left-hand number and a removed line has no right-hand one — but
        // never both, and no two lines in a diff share the same pair.
        <Line key={`${line.fromLine ?? '+'}:${line.toLine ?? '-'}`} line={line} />
      ))}
    </>
  );
}

export function DiffStats({ diff }: { diff: MarkdownDiff }) {
  if (diff.identical) return <Badge tone="outline">identical</Badge>;
  return (
    <span className="flex items-center gap-1.5">
      <Badge tone="success">+{diff.added}</Badge>
      <Badge tone="error">−{diff.removed}</Badge>
      {diff.truncated ? <Badge tone="warning">truncated</Badge> : null}
    </span>
  );
}

export function DiffView({ diff, className }: { diff: MarkdownDiff; className?: string }) {
  if (diff.identical) {
    return (
      <Alert tone="info" className={className}>
        These two are byte-identical. Nothing has changed between them.
      </Alert>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {diff.truncated ? (
        <Alert tone="warning" title="Too large to align line by line">
          The whole body is reported as one replacement. That is deliberate — a guessed alignment on
          a pair this size would be convincing and wrong.
        </Alert>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-secondary">
        <table className="w-full border-collapse">
          <tbody>
            {/* Hunks never overlap, so where one starts identifies it. */}
            {diff.hunks.map((hunk) => (
              <Hunk key={`${hunk.fromStart}-${hunk.toStart}`} hunk={hunk} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Names the two sides in the words the API used, so the refs stay legible. */
export function DiffHeading({ response }: { response: DiffResponse }) {
  const side = (ref: { ref: string; versionNumber: number | null }) =>
    ref.versionNumber === null ? ref.ref : `v${ref.versionNumber}`;

  return (
    <span className="flex flex-wrap items-center gap-2 text-sm text-secondary">
      <Badge tone="outline">{side(response.from)}</Badge>
      <span aria-hidden="true">→</span>
      <Badge tone="outline">{side(response.to)}</Badge>
      <DiffStats diff={response.diff} />
    </span>
  );
}
